import { Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { StatusProgressDots } from '@/components/DataDisplay/Indicator/StatusProgressDots';
import { Header } from '@/components/Header/Header';
import { MoreInformationContainer } from '@/components/MoreInformation/MoreInformationContainer';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectRightStartFeature } from '@/redux/configs/selectors';
import {
  selectBasicInfoStatus,
  selectSigningDocuments,
  selectUserData,
} from '@/redux/user-data/selectors';

import { DocumentStatus } from '@/types/document';

import { getIdentityDocumentCheckStatus } from '@/utils/document';
import { isExistingCustomer } from '@/utils/user';

import { Container, MainPageContainer } from './MoreInformation.styled';

export function MoreInformationPage() {
  const { t } = useTranslate();
  const theme = useTheme();
  const { requiredDocumentSections, user } = useAppSelector(selectUserData);
  const enabledRightStart = useAppSelector(selectRightStartFeature);
  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const { documents = [], clientReviewed } = user || {};
  const { documentsSigningStatus } = useAppSelector(selectSigningDocuments);
  const personalDetailsStatus = clientReviewed
    ? DocumentStatus.Approved
    : DocumentStatus.Pending;

  const existingCustomer = isExistingCustomer(user);
  const statusDots = useMemo(
    () => [
      ...(existingCustomer ? [personalDetailsStatus] : []),
      basicInfoStatus,
      ...(existingCustomer
        ? [getIdentityDocumentCheckStatus(documents, 3)]
        : []),
      ...requiredDocumentSections.map(({ status }) => status),
      documentsSigningStatus,
    ],
    [
      personalDetailsStatus,
      basicInfoStatus,
      documents,
      requiredDocumentSections,
      documentsSigningStatus,
      existingCustomer,
    ],
  );

  return (
    <MainPageContainer>
      <Header />
      <Container>
        <StatusProgressDots dots={statusDots} />
        <Typography variant="h1" textAlign="center" mt={1} mb={2}>
          {t('moreInfo.title')}
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          color={theme.palette.grey[800]}
          mb={4}
        >
          {enabledRightStart
            ? t('moreInfo.body.rightStart')
            : t('moreInfo.body')}
        </Typography>
        <MoreInformationContainer />
      </Container>
    </MainPageContainer>
  );
}

export default MoreInformationPage;
